import { useGlobalState } from 'context/GlobalStateContext'
import {
  simpleCreateNotification,
  simpleRemoveNotification,
  simpleSuccessNotification,
  simpleRestoreNotification,
  simpleRevertNotification,
} from 'hooks/notificationHandler'
import {
  useProjectFieldDesignChangesQuery,
  useDraftFieldDesignChangeMutation,
  useSubmitFieldDesignChangeMutation,
  useSoftRemoveFieldDesignChangeMutation,
  useEditFieldDesignChangeMutation,
  useSetFdcToFdcConcurrenceGroupMutation,
  SetFdcToFdcConcurrenceGroupMutationVariables,
  useSetFdcToFdcApprovalGroupMutation,
  useRevertFieldDesignChangeMutation,
  useCreateFdcCommentMutation,
  useFdcTrackerTableMetadataQuery,
  useRestoreFieldDesignChangeMutation,
} from 'schema'
import {
  DefaultQueryHookOptions,
  DefaultMutationHookOptions,
} from 'utils/defaults'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission, SharePointParentFolder } from 'shared'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useBooleanMap } from '../hooks/useBooleanMap'
import { createStrictContext } from 'utils/createContext'
import { useTriggerEarlyClosureNotification } from '../hooks/useTriggerEarlyClosureNotification'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'
import { FDCTrackerContext, FieldDesignChange, CheckFn } from './types'

const { Provider, useContextFn } =
  createStrictContext<FDCTrackerContext>('FDCTrackerContext')

export const useFDCTrackerContext = useContextFn

export const FDCTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const fieldDesignChanges = useProjectFieldDesignChangesQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      projectId: projectId!,
      input: { projectId: { equals: projectId } },
    },
  })
  const [triggerEarlyClosure, CreateDesignSubmittalModal] =
    useTriggerEarlyClosureNotification(fieldDesignChanges.refetch)
  const metadata = useFdcTrackerTableMetadataQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: { projectId: projectId! },
  })
  // revert
  const [revert, { loading: reverting }] = useRevertFieldDesignChangeMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      revertFieldDesignChange: {
        fieldDesignChange: { formattedNumber },
      },
    }) => simpleRevertNotification({ name: formattedNumber }),
  })
  const revertModal = useCIMSModalValues<FieldDesignChange>()

  // pending approvals
  const [approve] = useSetFdcToFdcApprovalGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      setFDCToFDCApprovalGroup: {
        approvalGroup: { name },
        approved,
      },
    }) =>
      simpleSuccessNotification(
        `Successfully ${approved ? 'approved' : 'reset'} ${name}`
      ),
  })
  const approvables = useBooleanMap(metadata?.data?.project?.fdcApprovalGroups)

  // pending level concurrences
  const [concurring, { add: addConcur, remove: removeConcur }] =
    useKeyedBooleans()
  const [setConcurrence] = useSetFdcToFdcConcurrenceGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      setFDCToFDCConcurrenceGroup: {
        concurred,
        concurrenceGroup: { name },
      },
    }) =>
      simpleSuccessNotification(
        `Successfully ${concurred ? 'concurred on' : 'rejected'} ${name}`
      ),
  })
  const concur: CheckFn<SetFdcToFdcConcurrenceGroupMutationVariables> = (
    key,
    variables
  ) => {
    addConcur(key)
    setConcurrence({ variables }).then(() => {
      removeConcur(key)
      fieldDesignChanges.refetch()
    })
  }
  const concurrables = useBooleanMap(
    metadata?.data?.project?.fdcConcurrenceGroups
  )

  // draft & submit
  const [draft, { loading: drafting }] = useDraftFieldDesignChangeMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      draftFieldDesignChange: {
        fieldDesignChange: { formattedNumber },
      },
    }) => simpleCreateNotification(`Draft ${formattedNumber}`),
  })
  const submitModal = useCIMSModalValues<FieldDesignChange>()
  const [submit, { loading: submitting }] = useSubmitFieldDesignChangeMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ submitFieldDesignChange: { fieldDesignChange } }) =>
      triggerEarlyClosure(fieldDesignChange, 'submitted'),
  })
  const canSubmit = useCheckPermission(Permission.CreateFieldDesignChange)

  // soft remove
  const [remove, { loading: removing }] =
    useSoftRemoveFieldDesignChangeMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        softRemoveFieldDesignChange: {
          fieldDesignChange: { formattedNumber },
        },
      }) => simpleRemoveNotification(formattedNumber),
    })

  // restore
  const [restore, { loading: restoring }] = useRestoreFieldDesignChangeMutation(
    {
      ...DefaultMutationHookOptions,
      onCompleted: ({
        restoreFieldDesignChange: {
          fieldDesignChange: { formattedNumber },
        },
      }) => simpleRestoreNotification(formattedNumber),
    }
  )

  // edit
  const [edit, { loading: editing }] = useEditFieldDesignChangeMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ editFieldDesignChange: { fieldDesignChange } }) =>
      triggerEarlyClosure(fieldDesignChange, 'edited'),
  })

  const canResolution = useCheckPermission(
    Permission.EditFieldDesignChangeResolution
  )
  const canEdit = useCheckPermission(Permission.EditFieldDesignChange)

  // comment
  const [comment, { loading: commenting }] = useCreateFdcCommentMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => fieldDesignChanges.refetch(),
  })
  const canCommentByPermission = useCheckPermission(
    Permission.CommentOnFieldDesignChange
  )
  const canComment =
    canCommentByPermission ||
    [...approvables.values()].some((b) => b) ||
    [...concurrables.values()].some((b) => b)

  const externalLinks = useExternalLinks<FieldDesignChange>({
    dataFn: ({ id, externalLinks, sharePointResourceWebUrl, number }) => ({
      recordId: id,
      externalLinks,
      folder: number.toString(),
      resource: { webUrl: sharePointResourceWebUrl },
    }),
    parent: SharePointParentFolder.FieldDesignChanges,
    refetch: fieldDesignChanges.refetch,
  })

  return (
    <Provider
      key="fdc-data-provider"
      value={{
        ...externalLinks,
        payItemGlobalId,
        fieldDesignChanges: {
          ...fieldDesignChanges,
          records: fieldDesignChanges?.data?.fieldDesignChanges?.records ?? [],
        },
        metadata: {
          ...metadata,
          ...(metadata?.data?.project ?? {
            __typename: 'Project',
            fdcApprovalGroups: [],
            fdcConcurrenceGroups: [],
            fdcConcurrenceLevels: [],
            fdcDisciplines: [],
            fdcQuickLinks: [],
          }),
        },
        mutations: {
          draft: { is: drafting, fn: draft },
          submit: {
            is: submitting,
            fn: submit,
            can: canSubmit,
            ...submitModal,
          },
          edit: { can: canEdit, canResolution, is: editing, fn: edit },
          remove: { is: removing, fn: remove },
          restore: { is: restoring, fn: restore },
          concur: { can: concurrables, fn: concur, is: concurring },
          approve: { can: approvables, fn: approve },
          revert: { fn: revert, is: reverting, ...revertModal },
          comment: { fn: comment, is: commenting, can: canComment },
        },
      }}
    >
      {CreateDesignSubmittalModal}
      {children}
    </Provider>
  )
}
